import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { LiliButton } from '@lili/lili-frontend-common';
import { STYLES } from '../consts';
import { useCreateBill } from '../hooks/useCreateBill';
import { useStore } from '../../../../../store';
import { trackMixPanel } from '../../../../../services/mixpanel.service';
import { CreationCtx } from '../pages';
import { useAccountSummaryQuery } from '../../../../../hooks/query/useAccountSummaryQuery';
import { getBillRecurringEventTypeText, isOneTimeBill } from '../../../BillDataUtils';
import { BillRecurringEventType } from '../../../types';

interface IProps {
  onSuccess: () => void;
}

export const ActionFooter = ({ onSuccess }: IProps) => {
  const { t } = useTranslation();
  const context = useContext(CreationCtx);
  const actionFlags = useStore(state => state.summary.actionFlags);
  const billData = useStore(state => state.billData);
  const setBillData = useStore(state => state.setBillData);
  const { mutateAsync: createBill, isLoading } = useCreateBill({ onSuccess });
  const {
    billId, billNumber, amount, dueDate, documentId, recurringPaymentData, paymentReminderData,
  } = useStore(state => state.billData);
  const { refetch: mutateSummary } = useAccountSummaryQuery();
  const selectedVendor = useStore(state => state.getSelectedVendor());

  const isPaymentFrequencyValid = useMemo(() => isOneTimeBill(recurringPaymentData?.paymentFrequency || BillRecurringEventType.ONE_TIME) || (recurringPaymentData?.recurringNumber), [recurringPaymentData]);

  const isFormValid = useMemo(() => {
    const reqProps = [billNumber, amount, dueDate, selectedVendor?.supplierId, selectedVendor?.name];
    return !reqProps.some(p => !p) && amount > 0 && isPaymentFrequencyValid;
  }, [selectedVendor, billId, billNumber, amount, dueDate, documentId, recurringPaymentData]);

  const isLoadingBillAttachment = useMemo(() => context.isLoadingBillAttachment, [context]);

  const handleSubmit = async () => {
    trackMixPanel('Act on add new bill', {
      Action: 'create bill',
      'Payment Frequency': getBillRecurringEventTypeText(recurringPaymentData?.paymentFrequency || BillRecurringEventType.ONE_TIME),
      'Payment reminder': paymentReminderData?.paymentReminderEnabled || false,
      'Number of payments': recurringPaymentData?.recurringNumber || 1,
    });
    await createBill(
      {
        payload: {
          billId,
          billNumber,
          amount,
          dueDate: dueDate?.toISOString() || new Date().toISOString(),
          supplierId: selectedVendor?.supplierId || '',
          supplierName: selectedVendor?.name || '',
          documentId,
          paymentReminderData: {
            paymentReminderEnabled: paymentReminderData?.paymentReminderEnabled || false,
          },
          recurringPaymentData: {
            paymentFrequency: recurringPaymentData?.paymentFrequency || BillRecurringEventType.ONE_TIME,
            recurringNumber: recurringPaymentData?.recurringNumber || 1,
          },
        },
      },
      {
        onSuccess: (res) => {
          setBillData({
            ...billData, billId: res.billId, supplierId: res.supplierId,
          });
          if (!actionFlags?.billCreated) mutateSummary();
        },
      },
    );
    onSuccess();
  };
  return (
    <div className={STYLES.FOOTER.WRAPPER}>
      <LiliButton
        disabled={!isFormValid || isLoadingBillAttachment}
        isLoading={isLoading || isLoadingBillAttachment}
        onClick={handleSubmit}
        className={STYLES.FOOTER.CTA}
        title={t('CREATE_BILL.FOOTER.CTA')}
        id='submitCreationFrom'
      />
    </div>
  );
};
